export type BlackoutType = 'PRODUCT' | 'VARIANT' | 'OPTION_VALUE' | 'MODIFIER' | 'BUNDLE';
export type BlackoutReason = 'OUT_OF_STOCK' | 'COMING_SOON';

export interface BlackoutItem {
  __typename: string;
  startingAt: string;
  expiry: string;
  reason: BlackoutReason;
  details?: string;
  productCode?: string;
  variantCode?: string;
  optionValueCode?: string;
  modifierCode?: string;
  bundleCode?: string;
  name?: string;
}

export interface BlackoutFormData {
  type: BlackoutType;
  itemCode: string;
  itemName?: string;
  reason: BlackoutReason;
  expiry: Date | null;
  startingAt?: Date;
}

export const BlackoutTypeLabels: Record<BlackoutType, string> = {
  PRODUCT: 'Product',
  VARIANT: 'Variant',
  OPTION_VALUE: 'Option Value',
  MODIFIER: 'Modifier',
  BUNDLE: 'Bundle',
};

export const BlackoutReasonLabels: Record<BlackoutReason, string> = {
  OUT_OF_STOCK: 'Out of Stock',
  // COMING_SOON is retired from new writes (backend coerces to OUT_OF_STOCK),
  // but historical Byte Graph records may still read back with this value,
  // so we keep the label for display.
  COMING_SOON: 'Coming Soon',
};

// Reasons selectable when creating a new blackout. COMING_SOON is
// intentionally excluded — see BlackoutReasonLabels above.
export const SELECTABLE_BLACKOUT_REASONS: BlackoutReason[] = ['OUT_OF_STOCK'];

// Top-level types a user can pick when CREATING a new snooze. VARIANT and
// OPTION_VALUE are excluded:
//   - VARIANT: reached by drilling into a parent product (Byte Graph's
//     autocompleteVariantIdentifiers only supports PLU search, which managers
//     don't have memorized).
//   - OPTION_VALUE: the autocomplete returns no usable results in practice;
//     managers should snooze the parent modifier or product instead.
// Historical blackouts can still come back with either type for read-side
// display purposes; the full BlackoutType union and BlackoutTypeLabels stay
// intact for that.
export const SELECTABLE_BLACKOUT_TYPES: BlackoutType[] = ['PRODUCT', 'BUNDLE', 'MODIFIER'];

export const BlackoutTypeHelp: Record<BlackoutType, string> = {
  PRODUCT: 'A standalone menu item (e.g., "Crunchy Taco").',
  BUNDLE: 'A combo or meal deal containing multiple products (e.g., "Taco Party Pack").',
  VARIANT:
    'A specific configuration of a product (e.g., "Large Coke — No Ice"). Snoozing a variant only hides that specific combination.',
  MODIFIER:
    'A group of choices attached to products (e.g., "Sauces"). Snoozing hides the entire group.',
  OPTION_VALUE:
    'A single choice within a modifier group (e.g., "Extra Cheese"). Snoozing hides this choice everywhere it appears.',
};

export const BLACKOUT_REASON_HELP =
  'Internal status only — not shown to customers. Marks an item as temporarily unavailable.';

export const getBlackoutType = (blackout: BlackoutItem): BlackoutType => {
  if (blackout.productCode) return 'PRODUCT';
  if (blackout.variantCode) return 'VARIANT';
  if (blackout.optionValueCode) return 'OPTION_VALUE';
  if (blackout.modifierCode) return 'MODIFIER';
  if (blackout.bundleCode) return 'BUNDLE';
  return 'PRODUCT'; // default
};

export const getItemCode = (blackout: BlackoutItem): string => {
  return (
    blackout.productCode ||
    blackout.variantCode ||
    blackout.optionValueCode ||
    blackout.modifierCode ||
    blackout.bundleCode ||
    ''
  );
};

export const getItemDisplayName = (blackout: BlackoutItem): string => {
  const code = getItemCode(blackout);
  if (blackout.name) {
    return `${code} - ${blackout.name}`;
  }
  return code;
};
